import { Combobox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { InputError } from 'components/UI/Inputs/InputError';
import { InputLabel } from 'components/UI/Inputs/InputLabel';
import { Tag } from 'components/UI/Tags/Tag';
import { EMAIL_REGEX } from 'consts';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { useLanguage } from 'features/hooks/useLanguage';
import { debounce } from 'lodash';
import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { LanguageType } from 'types';
import { useUserEmail } from '../../../../hooks/store';
import { useAlerts } from '../../../../hooks/ui';
import { UserCard } from './UserCard';

type Props = {
	users: string[];
	setUsers: (users: string[]) => void;
	excludeEmailAddresses: string[];
	error?: string;
	label?: string;
	placeholder?: string;
	canInviteExternalUser?: boolean;
	inviteCollaboratorSearch?: boolean;
};

export function UserSearchInput({
	users,
	setUsers,
	error,
	excludeEmailAddresses,
	label,
	placeholder,
	canInviteExternalUser = false,
	inviteCollaboratorSearch = false
}: Props) {
	const [inputValue, setInputValue] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const language = useLanguage();
	const { setError } = useAlerts();

	const updateAutocompleteQuery = useCallback(
		debounce((value: string) => {
			setSearchQuery(value);
		}, 500),
		[]
	);

	const autocompleteSuggestions = useQuery({
		enabled: searchQuery.length > 0,
		queryKey: ['autocompleteSuggestions', searchQuery, excludeEmailAddresses],
		queryFn: async () => {
			if (!inviteCollaboratorSearch) {
				return {
					users: []
				};
			}

			const result = await makeRequest<{
				users: UserResult[];
			}>({
				method: 'searchUsers',
				service: 'user',
				data: {
					userRequestByOrganization: {
						queryString: searchQuery,
						filter: {
							excludeEmailAddresses
						}
					}
				}
			});

			return result;
		},
		retry: false,
		staleTime: 1000 * 60
	});
	const currentUserEmail = useUserEmail();
	const isCurrentUserHso = currentUserEmail ? isHsoEmail(currentUserEmail) : false;

	const addEmail = (email: string) => {
		if (currentUserEmail && email.includes(currentUserEmail)) {
			setError({ message: DICTIONARY[language].errorAddingSelf });
			return;
		}

		const isHsoInternal = isCurrentUserHso && isHsoEmail(email);
		if (!canInviteExternalUser && !isHsoInternal) {
			setError({ message: DICTIONARY[language].errorAddingExternal });
			return;
		}

		const trimmedEmail = email.trim();
		if (
			trimmedEmail &&
			EMAIL_REGEX.test(trimmedEmail) &&
			!users.includes(trimmedEmail) &&
			!excludeEmailAddresses.includes(trimmedEmail)
		) {
			setUsers([...users, trimmedEmail]);
			setInputValue('');
			setSearchQuery('');
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		// Handle backspace to remove the last tag
		if (e.key === 'Backspace' && inputRef.current?.value === '' && users.length > 0) {
			const newUsers = [...users];
			newUsers.pop();
			setUsers(newUsers);
			return;
		}

		// Handle Enter key to add email
		if (e.key === 'Enter' && inputValue) {
			e.preventDefault();
			addEmail(inputValue);
			return;
		}

		// Handle comma to add email
		if (e.key === ',' && inputValue) {
			e.preventDefault();
			const emailToAdd = inputValue.replace(',', '');
			addEmail(emailToAdd);
			return;
		}
	};

	const pendingUsers = autocompleteSuggestions.data?.users.filter(
		user => !users.includes(user.emailAddress)
	);

	const isHsoInternal = isCurrentUserHso && isHsoEmail(inputValue);
	return (
		<div className="relative text-base">
			{label && <InputLabel label={label} />}
			<Combobox
				value={users}
				onChange={users => {
					setInputValue('');
					setSearchQuery('');

					setUsers(users);
				}}
				multiple
			>
				<div
					className={`flex flex-wrap w-full border rounded-md p-2 min-h-[4rem] gap-2 transition-all duration-200 ${
						error ? 'border-red-500' : 'border-gray-300 focus-within:border-blue-500'
					}`}
				>
					{users.length > 0 && (
						<div className="flex flex-wrap gap-2 items-center">
							{users.map(user => (
								<Tag
									key={user}
									title={user}
									active
									onDelete={() => {
										setUsers(users.filter(p => p !== user));
									}}
								/>
							))}
						</div>
					)}

					<Combobox.Input
						className="min-w-[200px] flex-1 border-none outline-none px-[0.8rem]"
						ref={inputRef}
						placeholder={placeholder}
						value={inputValue}
						onChange={e => {
							setInputValue(e.target.value);
							return updateAutocompleteQuery(e.target.value);
						}}
						onKeyDown={handleKeyDown}
						onBlur={() => {
							if (inputValue && (canInviteExternalUser || isHsoInternal)) {
								addEmail(inputValue);
							}
						}}
						autoFocus
					/>
				</div>
				<Combobox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg rounded-md overflow-auto focus:outline-none sm:text-sm max-h-[500px]">
					{pendingUsers &&
						pendingUsers.length > 0 &&
						pendingUsers.map(user => (
							<Combobox.Option
								key={user.userid}
								value={user.emailAddress}
								className={({ active }) => `
                                ${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}
                            `}
							>
								<UserCard user={user} />
							</Combobox.Option>
						))}

					{(canInviteExternalUser || isHsoInternal) &&
						inputValue &&
						EMAIL_REGEX.test(inputValue) &&
						(!pendingUsers ||
							!pendingUsers.some(user => user.emailAddress === inputValue)) && (
							<Combobox.Option
								value={inputValue}
								className={({ active }) => `
								${active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}
								p-3 cursor-pointer
							`}
							>
								<div className="flex items-center">
									<div className="ml-3">
										<p className="text-sm font-medium text-gray-900">
											{DICTIONARY[language].addEmail}: {inputValue}
										</p>
									</div>
								</div>
							</Combobox.Option>
						)}

					{pendingUsers &&
						pendingUsers.length === 0 &&
						searchQuery.length > 0 &&
						!(canInviteExternalUser && EMAIL_REGEX.test(inputValue)) && (
							<div className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500 text-base">
								{DICTIONARY[language].emptyResult}
							</div>
						)}
				</Combobox.Options>
			</Combobox>
			<InputError error={error} />
		</div>
	);
}

function isHsoEmail(email: string) {
	const hsoDomains = [
		'ous-hf.no',
		'vestreviken.no',
		'sykehuset-innlandet.no',
		'sihf.no',
		'sshf.no',
		'ahus.no',
		'sthf.no',
		'so-hf.no',
		'siv.no',
		'sunnaas.no',
		'sahf.no'
	];

	const emailDomain = email.split('@')[1]?.toLowerCase();
	return emailDomain ? hsoDomains.includes(emailDomain) : false;
}

export type UserResult = {
	userid: string;
	userFirstName: string;
	userSirName: string;
	emailAddress: string;
	department: string;
	phoneNumber: string;
	organization: string;
};

const DICTIONARY: Record<LanguageType, Record<string, string>> = {
	[LanguageType.English]: {
		emptyResult: 'No results found.',
		addEmail: 'Add email address',
		errorAddingExternal: 'You cannot add external users.',
		errorAddingSelf: 'You cannot add yourself as a collaborator.'
	},
	[LanguageType.Norwegian]: {
		emptyResult: 'Ingen brukere funnet.',
		addEmail: 'Legg til e-postadresse',
		errorAddingExternal: 'Du kan ikke legge til eksterne brukere.',
		errorAddingSelf: 'Du kan ikke legge til deg selv som samarbeidspartner.'
	}
} as const;
